import { sendGTMEvent } from '@next/third-parties/google'

/**
 * Push a GTM dataLayer event.
 *
 * Single entry point for all custom events — keeps naming and params
 * consistent and gives us one place to fan out to other destinations later.
 */
export function track(event, params = {}) {
  sendGTMEvent({ event, ...params })
}

/**
 * Standard payload for clicks out to an external news article.
 * `context` adds surface-specific fields (story_slug, panel_title, surface…).
 */
export function trackOutboundArticle(article, context = {}) {
  track('article_outbound_click', {
    article_url: article?.url,
    article_type: article?.article_type,
    outlet_slug: article?.outlet?.slug,
    outlet_name: article?.outlet?.name,
    outlet_bias: article?.outlet?.bias,
    outlet_reliability: article?.outlet?.reliability,
    is_paywalled: article?.outlet?.is_paywalled ?? false,
    ...context,
  })
}

/**
 * Push a GTM event at most once per browser session.
 *
 * Use for events tied to page mounts (e.g. confirmation pages) that would
 * otherwise re-fire on every revisit or reload.
 */
export function trackOnce(event, params = {}) {
  if (typeof window === 'undefined') return
  const key = `gtm_once_${event}`
  try {
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
  } catch {
    // sessionStorage unavailable (private mode) — fall through and fire
  }
  track(event, params)
}

// Browser-side errors, as `client_error` (2026-10-23). At most five a page
// load and each message once, so one broken loop can't flood GA4. Noise
// that isn't ours is dropped: browser extensions, the cross-origin "Script
// error." with no detail, and the harmless ResizeObserver loop warning.
const reportedErrors = new Set()
export function reportClientError(error, source, file = '') {
  if (typeof window === 'undefined') return
  const message = String(error?.message || error || 'Unknown error')
  if (
    /^Script error\.?$/.test(message) ||
    /ResizeObserver loop/.test(message) ||
    /extension:\/\//.test(file || error?.stack || '')
  )
    return
  const key = `${source}:${message}`
  if (reportedErrors.has(key) || reportedErrors.size >= 5) return
  reportedErrors.add(key)
  track('client_error', {
    error_message: message.slice(0, 100),
    error_source: source,
    error_digest: error?.digest || '',
  })
}
